import { useCallback, useMemo } from 'react';

import { useStoreContext } from 'features/store';

import { ISanityVendorConfigs } from '@rbi-ctg/menu';
import { ItemAvailabilityStatus } from 'enums/menu';
import { Maybe } from 'generated/sanity-graphql';
import usePosVendor from 'hooks/menu/use-pos-vendor';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { selectors, useAppSelector } from 'state/global-state';
import { LoyaltyOffer, LoyaltyReward, WithIncentives, isLoyaltyOffer } from 'state/loyalty/types';
import { isDiscountOffer } from 'state/loyalty/utils';
import { useMenuContext } from 'state/menu';
import { useMainMenuContext } from 'state/menu/main-menu';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { isEntryAvailable } from 'state/unified-menu/utils';
import { itemIsAvailable } from 'utils/availability';
import { PosVendors } from 'utils/vendor-config';

import {
  checkIncentiveAvailableForDayPart,
  makeIncentiveUnavailableMessage,
} from './incentive-details/utils';

const VENDORS_TO_IGNORE_AVAILABILITY_CHECK = [
  PosVendors.NCR,
  PosVendors.NCR_DELIVERY,
  PosVendors.QDI,
  PosVendors.QDI_DELIVERY,
];

const shouldSkipAvailabilityCheck = ({
  vendor,
  offer,
}: {
  vendor: PosVendors;
  offer: WithIncentives;
}) => Boolean(VENDORS_TO_IGNORE_AVAILABILITY_CHECK.includes(vendor) && isDiscountOffer(offer));

type GetIncentiveUnavailableMessageInput = {
  incentive: LoyaltyReward | LoyaltyOffer;
  pointCost?: number;
  locked?: Maybe<boolean>;
};

export const useLoyaltyIncentivesAvailabilityUtils = () => {
  const {
    checkItemAvailability,
    showBurgersForBreakfast: burgersForBreakfastEnabled,
  } = useMenuContext();
  const { activeDayParts, dayParts } = useMainMenuContext();
  const { isStoreOpenAndAvailable, prices } = useStoreContext();
  const { vendor } = usePosVendor();
  const stagedCartPoints = useAppSelector(selectors.loyalty.selectStagedCartPoints);
  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const { indexedMenu, indexedRewards } = useUnifiedMenuContext();
  const { posServiceMode } = useMenuQueryOptions();

  const isEntityAvailable = useCallback(
    (entityId: string) => {
      const rewardEntry = indexedRewards.find(entityId);
      if (rewardEntry) {
        const menuEntry = indexedMenu.find(rewardEntry.options[0].entryId);
        return isEntryAvailable(
          menuEntry,
          posServiceMode,
          activeDayParts.map(d => d.key)
        );
      }

      const menuEntry = indexedMenu.find(entityId);
      return (
        !!menuEntry &&
        isEntryAvailable(
          menuEntry,
          posServiceMode,
          activeDayParts.map(d => d.key)
        )
      );
    },
    [activeDayParts, indexedMenu, indexedRewards, posServiceMode]
  );

  const checkIncentiveAvailability = useCallback(
    async (itemId: string) => {
      if (isUnifiedMenuEnabled) {
        const [, ...sanitizedId] = itemId.split('-');
        return isEntityAvailable(itemId) || isEntityAvailable(sanitizedId.join('-'));
      }

      // Check store availability for incentive
      if (isStoreOpenAndAvailable && itemId) {
        const { availabilityStatus } = await checkItemAvailability(itemId);

        return availabilityStatus === ItemAvailabilityStatus.AVAILABLE;
      }
      return false;
    },
    [isEntityAvailable, checkItemAvailability, isStoreOpenAndAvailable, isUnifiedMenuEnabled]
  );

  const checkLoyaltyIncentiveAvailability = useCallback(
    (incentive: LoyaltyReward | LoyaltyOffer) => {
      if (isUnifiedMenuEnabled) {
        return isEntityAvailable(incentive._id);
      }
      let checkResult = true;

      if (!!incentive?.vendorConfigs && !!vendor && !!prices) {
        checkResult = itemIsAvailable(
          {
            ...incentive,
            name: undefined,
            vendorConfigs: incentive.vendorConfigs as ISanityVendorConfigs,
          },
          vendor,
          prices
        );
      }

      return checkResult;
    },
    [isUnifiedMenuEnabled, vendor, prices, isEntityAvailable]
  );

  const checkLoyaltyOfferAvailability = useCallback(
    (offer: LoyaltyOffer) => {
      return !vendor || shouldSkipAvailabilityCheck({ vendor, offer })
        ? true
        : checkLoyaltyIncentiveAvailability(offer);
    },
    [vendor, checkLoyaltyIncentiveAvailability]
  );

  const getIncentiveUnavailableMessage = useCallback(
    ({ incentive, pointCost, locked }: GetIncentiveUnavailableMessageInput) => {
      if (!incentive) {
        return;
      }
      const isIncentiveAvailable = isLoyaltyOffer(incentive)
        ? checkLoyaltyOfferAvailability(incentive)
        : checkLoyaltyIncentiveAvailability(incentive);

      const isAvailableForDayPart = checkIncentiveAvailableForDayPart({
        activeDayParts,
        burgersForBreakfastEnabled,
        incentive,
      });

      const incentiveUnavailableMessage = makeIncentiveUnavailableMessage({
        burgersForBreakfastEnabled,
        dayParts,
        incentive,
        isAvailableForDayPart,
        isIncentiveAvailable,
        locked,
        pointCost,
        stagedCartPoints,
      });

      return incentiveUnavailableMessage;
    },
    [
      activeDayParts,
      burgersForBreakfastEnabled,
      checkLoyaltyIncentiveAvailability,
      checkLoyaltyOfferAvailability,
      dayParts,
      stagedCartPoints,
    ]
  );

  // When dependencies have been loaded
  const ready = useMemo(() => !!activeDayParts.length, [activeDayParts]);

  return {
    checkIncentiveAvailability,
    checkLoyaltyOfferAvailability,
    getIncentiveUnavailableMessage,
    ready,
  };
};

export const useLoyaltyIncentivesAvailability = (
  incentive?: LoyaltyReward | LoyaltyOffer,
  pointCost?: number,
  locked?: Maybe<boolean>
) => {
  const {
    checkIncentiveAvailability,
    checkLoyaltyOfferAvailability,
    getIncentiveUnavailableMessage,
  } = useLoyaltyIncentivesAvailabilityUtils();

  const incentiveUnavailableMessage = useMemo(
    () => incentive && getIncentiveUnavailableMessage({ locked, pointCost, incentive }),
    [getIncentiveUnavailableMessage, locked, pointCost, incentive]
  );

  return {
    checkIncentiveAvailability,
    checkLoyaltyOfferAvailability,
    incentiveUnavailableMessage,
  };
};

export type CheckLoyaltyOfferAvailabilityType = ReturnType<
  typeof useLoyaltyIncentivesAvailabilityUtils
>['checkLoyaltyOfferAvailability'];
